import { Link } from 'react-router';
import { motion } from 'framer-motion';
import { Trans } from '@lingui/react/macro';

interface TryForFreeButtonProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  text?: React.ReactNode;
  href?: string;
  showArrow?: boolean;
  className?: string;
  onClick?: () => void;
}

export default function TryForFreeButton({
  size = 'md',
  text,
  href = '/sign-up',
  showArrow = false,
  className = '',
  onClick,
}: TryForFreeButtonProps) {
  const isAnimated = false;

  // Use provided text or default to Trans component
  const displayText = text ?? null;
  const sizeClasses = {
    sm: 'px-4 py-2 h-9 text-sm',
    md: 'px-6 py-3 h-11 text-base',
    lg: 'px-8 sm:px-12 py-4 sm:py-5 h-12 sm:h-14 text-base sm:text-lg',
    xl: 'px-12 sm:px-16 py-5 sm:py-6 h-14 sm:h-16 text-lg sm:text-xl',
  };

  const buttonContent = (
    <motion.span
      className={`inline-flex items-center whitespace-nowrap ${sizeClasses[size]} font-semibold rounded-xl bg-gradient-to-r from-[#8B793D] via-[#8B793D]/80 to-[#8B793D] text-white shadow-lg hover:shadow-xl relative overflow-visible group ${className}`}
      whileHover={isAnimated ? { scale: 1.08 } : undefined}
      whileTap={isAnimated ? { scale: 0.95 } : undefined}
      animate={isAnimated ? {
        scaleX: [1, 1, 1.05, 0.95, 1.02, 1],
        scaleY: [1, 1, 0.95, 1.05, 0.98, 1],
        rotate: [0, 0, 2, -2, 1, 0],
        borderRadius: ['12px', '12px', '16px', '8px', '14px', '12px'],
        boxShadow: [
          '0 10px 30px rgba(139, 121, 61, 0.4)',
          '0 10px 30px rgba(139, 121, 61, 0.4)',
          '0 15px 50px rgba(139, 121, 61, 0.7)',
          '0 15px 50px rgba(139, 121, 61, 0.7)',
          '0 12px 35px rgba(139, 121, 61, 0.5)',
          '0 10px 30px rgba(139, 121, 61, 0.4)',
        ],
      } : undefined}
    >
      {/* Outer glow ring - breathing effect */}
      {isAnimated && (
        <motion.div
          className="absolute -inset-1 rounded-xl bg-gradient-to-r from-[#8B793D]/30 via-[#D4AF37]/30 to-[#8B793D]/30 blur-sm"
          animate={{
            scale: [1, 1, 1.15, 1.1, 1.05, 1],
            opacity: [0.3, 0.3, 0.8, 0.7, 0.5, 0.3],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut',
            times: [0, 0.33, 0.5, 0.66, 0.83, 1],
          }}
        />
      )}

      {/* Text content */}
      <motion.span
        className="relative z-10 flex items-center gap-2"
        animate={isAnimated ? {
          scale: [1, 1, 1.05, 1.02, 1.01, 1],
        } : undefined}
        transition={isAnimated ? {
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut',
          times: [0, 0.33, 0.5, 0.66, 0.83, 1],
        } : undefined}
      >
        {displayText ?? <Trans>TRY FOR FREE</Trans>}
        {showArrow && isAnimated && (
          <motion.span
            animate={{
              x: [0, 0, 6, 4, 2, 0],
              scale: [1, 1, 1.2, 1.1, 1.05, 1],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
              times: [0, 0.33, 0.5, 0.66, 0.83, 1],
            }}
          >
            →
          </motion.span>
        )}
        {showArrow && !isAnimated && <span>→</span>}
      </motion.span>

      {/* Pulse effect on hover */}
      {isAnimated && (
        <div className="absolute inset-0 rounded-xl bg-white/30 scale-0 group-hover:scale-100 transition-transform duration-300" />
      )}
    </motion.span>
  );

  if (onClick) {
    return (
      <button onClick={onClick} className="inline-block">
        {buttonContent}
      </button>
    );
  }

  return (
    <Link to={href} className="inline-block">
      {buttonContent}
    </Link>
  );
}

